import type { SummativeTestAlertConfigType } from "@configs/custom-app-config-schema";
import { groupTagsByType } from "@helpers/content/tag";
import { getShowStudyPackViews } from "@helpers/exams";
import { useAppStore } from "@hooks/useAppStore";
import type { BundleContext, ProductType } from "@memorang/types/src/bundle";
import type { MixedItemDist } from "@memorang/types/src/tag";
import { trackCustomEvent } from "analytics";

const trackBundleContext = ({ id, name }: BundleContext) => {
	const userProperty = {
		bundle_context_id: id,
		bundle_context_title: name,
	};
	trackCustomEvent(
		{ eventName: "Bundle_Context_Switched", id, name },
		userProperty,
	);
};

export const getSummativeTestAlertConfig = ({
	hasQuiz,
	hasPracticeTests,
	hasBothQuizAndPracticeTests,
	summativeTestAlertConfig,
}: {
	hasQuiz: boolean;
	hasPracticeTests: boolean;
	hasBothQuizAndPracticeTests: boolean;
	summativeTestAlertConfig: SummativeTestAlertConfigType;
}) => {
	if (hasBothQuizAndPracticeTests) {
		return summativeTestAlertConfig.quizAndPracticeTest;
	}
	if (hasQuiz) {
		return summativeTestAlertConfig.quiz;
	}
	if (hasPracticeTests) {
		return summativeTestAlertConfig.practiceTest;
	}
	return null;
};

const getProductItemDescription = (bundleType: ProductType) => {
	switch (bundleType) {
		case "STUDY_PACK":
			return "Study-Pack";
		case "PRACTICE_TEST":
			return "Practice Tests";
		case "EXAM":
			return "Exam-Prep";
		default:
			return "";
	}
};

export const getQuickActionsChips = (studyPackCollectionId?: number) => {
	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);
	const chips = [
		...(showStudyPackViews
			? [
					{
						label: "Study weak areas",
						type: "weak" as const,
						icon: "alert-decagram-outline",
						tooltip:
							"Focus on areas where you are weak to improve your overall performance",
					},
				]
			: []),
		{
			label: "View progress",
			type: "progress" as const,
			icon: "chart-timeline-variant-shimmer",
			tooltip: "Track your progress and see how you are improving",
		},
		{
			label: "View rank",
			type: "leaderboard" as const,
			icon: "podium-gold",
			tooltip: "See how you rank against others",
		},
	];
	return chips;
};

export const getWeakTagsIds = (mixedItemDist: MixedItemDist) => {
	const { tags, freeTagIds } = mixedItemDist;
	const groupedTagsByType = groupTagsByType(tags, "Progress", freeTagIds);
	const allTags = Object.keys(groupedTagsByType).flatMap(
		(key) => groupedTagsByType[key],
	);

	const sortedMasteryLevels = useAppStore.getState().app.masteryLevels!;
	const lowLevelTags = sortedMasteryLevels.filter((tag) => tag.level === "low");

	const lowScoredTags = allTags.filter((item) => {
		const distKeys = Object.keys(item.distribution);
		return lowLevelTags.some(
			(tag) => distKeys.includes(tag.id) && item.distribution[tag.id] > 0,
		);
	});

	const ids = lowScoredTags.map((tag) => tag.id);
	return ids;
};
export { trackBundleContext, getProductItemDescription };
